import type {
  KpiMonth,
  MonetizationTerm,
  OperationalTerm,
  ScoreDetail,
  ScoreParameters,
} from "../types/chargingStationDashboard";
import { DASH, formatEuro, formatNumber, formatPercent, formatSignedPercent } from "./fleetDerivations";

/**
 * BOF-76 — turning the KPI scores into sentences a reader can check.
 *
 * A score on its own says only *that* it moved. FR-050 asks for *why*, so every term is shown with its
 * input, what it was measured against, how far from that it landed, and how many points it contributed.
 * Everything here is derived from what the API returned — the parameters included — so the page never
 * holds a second copy of the model that could drift from the one the server scored with (FR-051).
 */

export const OPERATIONAL_TERMS: OperationalTerm[] = ["activation", "cpOnlinePerClient", "cpTotalPerClient", "clientGrowth"];
export const MONETIZATION_TERMS: MonetizationTerm[] = ["feeGrowth", "feePerClient", "feePerCpTotal"];

export const TERM_LABEL: Record<OperationalTerm | MonetizationTerm, string> = {
  activation: "Activation rate",
  cpOnlinePerClient: "Charge points online per client",
  cpTotalPerClient: "Charge points total per client",
  clientGrowth: "Client growth",
  feeGrowth: "Service fee growth",
  feePerClient: "Service fee per client",
  feePerCpTotal: "Service fee per charge point",
};

export const formatScore = (value: number | null | undefined): string => formatNumber(value, 1);

/**
 * How far a term landed from its target, in words.
 *
 * The API returns the sub-score, not the raw ratio. Below target the two are the same number; above it
 * the sub-score is `1 + excess × bonusRate`, so the excess is recovered by dividing the bonus back out —
 * "activation 9% above target" is then the input's own distance from its target, not the damped credit
 * it earned for it.
 */
export const subScoreStatement = (subScore: number | null | undefined, bonusRate: number): string => {
  if (subScore == null || !Number.isFinite(subScore)) return DASH;
  if (subScore <= 0) return "earns nothing";
  const EPSILON = 0.0005;
  if (Math.abs(subScore - 1) < EPSILON) return "on target";
  if (subScore < 1) return `${formatNumber((1 - subScore) * 100)}% below target`;
  const excess = bonusRate > 0 ? (subScore - 1) / bonusRate : 0;
  return `${formatNumber(excess * 100)}% above target`;
};

/** Points a term contributed to its score: weight × sub-score × 100. */
export const contribution = (subScore: number | null | undefined, weight: number | null | undefined): number | null =>
  subScore == null || weight == null ? null : weight * subScore * 100;

export interface TermRow {
  term: OperationalTerm | MonetizationTerm;
  label: string;
  /** The input, formatted in its own unit. */
  input: string;
  /** What the input was measured against, formatted in the same unit. */
  target: string;
  subScore: number | null;
  statement: string;
  weight: number | null;
  points: number | null;
}

const perClient = (v: number | null) => formatNumber(v, 2);
const euroPrecise = (cents: number | null) => formatEuro(cents, 2);

/** One row per term of a score, in the model's order, whether or not the score could be computed. */
export const operationalRows = (month: KpiMonth, p: ScoreParameters, detail: ScoreDetail | null): TermRow[] => {
  const i = month.inputs;
  const input: Record<OperationalTerm, string> = {
    activation: formatPercent(i.activationRate),
    cpOnlinePerClient: perClient(i.cpOnlinePerClient),
    cpTotalPerClient: perClient(i.cpTotalPerClient),
    clientGrowth: formatSignedPercent(i.clientGrowth),
  };
  const target: Record<OperationalTerm, string> = {
    activation: formatPercent(p.operationalTargets.activation, 0),
    cpOnlinePerClient: perClient(p.operationalTargets.cpOnlinePerClient),
    cpTotalPerClient: perClient(p.operationalTargets.cpTotalPerClient),
    clientGrowth: formatPercent(p.operationalTargets.clientGrowth, 0),
  };
  return OPERATIONAL_TERMS.map((term) => row(term, input[term], target[term], detail, p.operationalWeights[term], p));
};

export const monetizationRows = (month: KpiMonth, p: ScoreParameters, detail: ScoreDetail | null): TermRow[] => {
  const i = month.inputs;
  const input: Record<MonetizationTerm, string> = {
    feeGrowth: formatSignedPercent(i.feeGrowth),
    feePerClient: euroPrecise(i.feePerClient),
    feePerCpTotal: euroPrecise(i.feePerCpTotal),
  };
  // With no history the baseline falls back to the month's own value, which scores exactly on target —
  // said as such, so a 1.0 there is not mistaken for a month that matched its past.
  const baseline = (value: number | null) =>
    value == null ? `this month (no prior ${p.baselineMonths} months)` : `${euroPrecise(value)} (${p.baselineMonths}-month avg.)`;
  const target: Record<MonetizationTerm, string> = {
    feeGrowth: formatPercent(p.feeGrowthTarget, 0),
    feePerClient: baseline(i.feePerClientBaseline),
    feePerCpTotal: baseline(i.feePerCpTotalBaseline),
  };
  return MONETIZATION_TERMS.map((term) => row(term, input[term], target[term], detail, p.monetizationWeights[term], p));
};

const row = (
  term: OperationalTerm | MonetizationTerm,
  input: string,
  target: string,
  detail: ScoreDetail | null,
  weight: number | undefined,
  p: ScoreParameters,
): TermRow => {
  const subScore = detail?.subScores?.[term] ?? null;
  return {
    term,
    label: TERM_LABEL[term],
    input,
    target,
    subScore,
    statement: subScoreStatement(subScore, p.excessBonusRate),
    weight: weight ?? null,
    points: contribution(subScore, weight),
  };
};

/**
 * Why a month's scores could not be computed, root causes first (FR-052).
 *
 * A score is only ever missing because one of its inputs is, and each input is missing for a nameable
 * reason. Listing them turns "unavailable" into something a reader can act on — usually "the month's
 * credit-note run has not completed yet".
 */
export const unavailableReasons = (month: KpiMonth): { operational: string[]; monetization: string[] } => {
  const operational: string[] = [];
  const monetization: string[] = [];
  const noClients = month.invoicedClients == null;
  const noCensus = month.chargePointsTotal == null;
  const noFee = month.serviceFeeCents == null;

  if (noClients) {
    const text = "No completed credit-note run for this month, so there is no invoiced-client count.";
    operational.push(text);
    monetization.push(text);
  } else if (month.invoicedClients === 0) {
    const text = "The credit-note run invoiced no clients, so per-client figures have no denominator.";
    operational.push(text);
    monetization.push(text);
  }
  if (!noClients && month.inputs.previousClients == null) {
    operational.push("No invoiced-client count for the previous month, so client growth cannot be measured.");
  } else if (month.inputs.previousClients === 0) {
    operational.push("The previous month invoiced no clients, so client growth has no base.");
  }
  if (noCensus) {
    const text = "No charge-point snapshot was recorded for this month.";
    operational.push(text);
    monetization.push(text);
  } else if (month.chargePointsTotal === 0) {
    const text = "The charge-point snapshot recorded no charge points.";
    operational.push(text);
    monetization.push(text);
  }
  if (noFee) {
    monetization.push("No day of this month has been calculated, so there is no service fee.");
  } else if (month.inputs.feeGrowth == null) {
    monetization.push("No service fee for the previous month, so fee growth cannot be measured.");
  }

  return {
    operational: month.scores.operational ? [] : withFallback(operational),
    monetization: month.scores.monetization ? [] : withFallback(monetization),
  };
};

const withFallback = (reasons: string[]) => (reasons.length ? reasons : ["An input to this score is unavailable."]);

/** The Overall blend written out with the month's own numbers, so the arithmetic can be followed. */
export const overallFormula = (p: ScoreParameters, operational: number | null, monetization: number | null): string => {
  const { operational: wo, monetization: wm, imbalancePenalty: k } = p.overallBlend;
  if (operational == null || monetization == null) {
    return `${wo} × Operational + ${wm} × Monetization − ${k} × |Operational − Monetization|`;
  }
  return (
    `${wo} × ${formatScore(operational)} + ${wm} × ${formatScore(monetization)}` +
    ` − ${k} × |${formatScore(operational)} − ${formatScore(monetization)}|`
  );
};
